import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Box,
  Text,
  Stack,
  HStack,
  Button,
  VStack,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";
import { COLORS } from "../../../constants/colors";
import { MdContentCopy } from "react-icons/md";
import { successNotifier } from "../../../components/NotificationHandler";
import { PROTECTED_PATHS } from "../../../app/constants";
import { useNavigate } from "react-router-dom";

const NinePsbModal = ({ isOpen, onClose, paymentData, redirectPath }) => {
  const [copied, setCopied] = useState("");
  const [timeLeft, setTimeLeft] = useState(null);
  const cardBg = useColorModeValue(COLORS.primary, COLORS.white);
  const bgColor = useColorModeValue("#f7f7f7", "#2d3748");
  const { ID_CARD } = PROTECTED_PATHS;
  const navigate = useNavigate();

  // Countdown timer effect - must be before any early returns
  useEffect(() => {
    if (!isOpen || !paymentData || !paymentData.data) return;

    const expiryDateValue = paymentData.data.customer?.account?.expiry?.date;
    if (!expiryDateValue) {
      setTimeLeft(null);
      return;
    }

    const expiryDate = new Date(expiryDateValue);

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = expiryDate.getTime() - now;

      if (distance > 0) {
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeLeft("Expired");
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [isOpen, paymentData]);

  if (!paymentData || !paymentData.data) {
    return null;
  }

  const { customer, order } = paymentData.data;
  const account = typeof customer === "object" ? customer?.account : null;
  const hasAccountNumber = Boolean(account?.number);
  const hasAmount =
    typeof order === "object" && order?.amount !== undefined && order?.amount !== null;
  const isCardCreatedStatus =
    customer === "Card Created" && order === "Order Successful ";
    console.log("Payment Data",paymentData);
  const isPaymentSuccess =
    paymentData?.success === true || paymentData?.success === "success";
  const showSimpleSuccessState =
    isCardCreatedStatus || (isPaymentSuccess && (!hasAccountNumber || !hasAmount));

    console.log("simple", showSimpleSuccessState);

  const expiryDate = new Date(account?.expiry?.date);
  const formattedExpiryTime = account?.expiry?.date
    ? expiryDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "";

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(""), 2000);
  };

  const InfoRow = ({ label, value, copiable = false, copyValue = null }) => (
    <VStack align="flex-start" spacing="2" w="100%">
      <Text fontSize="sm" fontWeight="600" color="gray.500">
        {label}
      </Text>
      <HStack w="100%" justify="space-between" align="center">
        <Text
          fontSize="md"
          fontWeight="bold"
          color={cardBg}
          wordBreak="break-all"
        >
          {value}
        </Text>
        {copiable && (
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<MdContentCopy />}
            onClick={() => handleCopy(copyValue || value, label)}
            colorScheme={copied === label ? "green" : "gray"}
          >
            {copied === label ? "Copied" : "Copy"}
          </Button>
        )}
      </HStack>
    </VStack>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent bg="white" maxH="90vh" overflowY="auto">
        <ModalHeader
          bg={COLORS.green}
          color="white"
          borderRadius="md"
          textAlign="center"
        >
          {showSimpleSuccessState
            ? "Access Card Request Status"
            : "Bank Transfer Payment Details"}
        </ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: "none" }} />
        <ModalBody py="6">
          <Stack spacing="6">
            {showSimpleSuccessState ? (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <VStack align="flex-start" spacing="1">
                  <AlertDescription fontSize="md" fontWeight="700">
                    Your Access card request was created successfully.
                  </AlertDescription>
                  <AlertDescription fontSize="sm">
                    No transfer is required right now. You can close this window
                    and track progress from your Access card page.
                  </AlertDescription>
                </VStack>
              </Alert>
            ) : (
              <>
                {/* Merged Alert Section with Countdown */}
                <Alert
                  status="warning"
                  borderRadius="md"
                  bg="#fff5e6"
                  borderColor="#f6ad55"
                  borderWidth="2px"
                >
                  <AlertIcon color="#c05621" />
                  <VStack align="flex-start" spacing="2" w="100%" ml="2">
                    <AlertDescription fontSize="md" fontWeight="bold">
                      ⏱️ Time Remaining:{" "}
                      <Text as="span" color="#c05621" fontWeight="900">
                        {timeLeft || "--"}
                      </Text>
                    </AlertDescription>
                    <AlertDescription fontSize="sm">
                      This virtual account expires at {formattedExpiryTime} on{" "}
                      {expiryDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </AlertDescription>
                    <AlertDescription fontSize="sm" fontWeight="600">
                      Transfer exactly amount to complete payment
                    </AlertDescription>
                  </VStack>
                </Alert>

                {/* Account Information Section */}
                <Box
                  bg={bgColor}
                  p="4"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Text fontWeight="bold" mb="4" fontSize="md" color={cardBg}>
                    Virtual Account Details
                  </Text>
                  <Stack spacing="4">
                    <InfoRow label="Account Name" value={account?.name} />
                    <InfoRow
                      label="Account Number"
                      value={account?.number}
                      copiable={true}
                    />
                    <InfoRow label="Bank" value={account?.bank} />

                    <InfoRow
                      label="Amount to Pay"
                      value={`₦${order?.amount?.toLocaleString()}`}
                      copiable={true}
                      copyValue={order?.amount?.toString()}
                    />
                  </Stack>
                </Box>

                {/* Instructions */}
                <Box
                  bg="blue.50"
                  p="4"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontWeight="bold" mb="2" fontSize="sm" color="blue.900">
                    Instructions:
                  </Text>
                  <Stack spacing="1" fontSize="sm" color="blue.900">
                    <Text>1. Copy the account number above</Text>
                    <Text>
                      2. Transfer exactly ₦{order?.amount?.toLocaleString()} to
                      the account
                    </Text>
                    <Text>
                      3. Your card request will be processed upon payment
                      confirmation
                    </Text>
                  </Stack>
                </Box>
              </>
            )}

            {/* Close Button */}
            <Button
              w="100%"
              bg={COLORS.green}
              color="white"
              onClick={() => {
                successNotifier(
                  showSimpleSuccessState
                    ? "Access card created successfully."
                    : "Payment instructions acknowledged. Proceed with your transfer.",
                );
                navigate(redirectPath || ID_CARD);
                onClose();
              }}
              _hover={{ opacity: 0.85 }}
              size="md"
            >
              Got It
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NinePsbModal;
